import { useSelector } from "react-redux";

import { LockIcon } from "@/assets/icons";
import { UnlockPremiumModal } from "@/components/dataset/datasetExplorer/UnlockPremiumModal";
import { Markdown } from "@/components/ui/markdown";
import { MESSAGE } from "@/constants/message";
import {
  ExecutiveSummaryError,
  ExecutiveSummaryFeedback
} from "@/hooks/analysisSummary/useExecutiveSummary";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";

import { AISummaryError } from "./AISummaryError";
import { useMarkdownSummary } from "./useMarkdownSummary";

import { AISummaryFeedbackActions } from "./feedback/AISummaryFeedbackActions";

const ChunkSkeleton = () => {
  return (
    <div
      data-testid="paragraph-skeleton"
      className="mt-3 animate-pulse space-y-1"
    >
      <div className="h-5 bg-whisper rounded w-full" />
      <div className="grid grid-cols-3 gap-1">
        <div className="h-5 bg-whisper rounded w-full" />
        <div className="h-5 bg-whisper rounded w-full" />
        <div className="h-5 bg-whisper rounded w-full" />
      </div>
      <div className="grid grid-cols-2 gap-1">
        <div className="h-5 bg-whisper rounded w-full" />
        <div className="h-5 bg-whisper rounded w-full" />
      </div>
      <div className="h-5 bg-whisper rounded w-2/3" />
    </div>
  );
};

interface AISummaryProps {
  chunks: string[];
  isStreaming: boolean;
  permissionAI: boolean;
  isExpanded: boolean;
  onCollapse?: () => void;
  initialFeedback?: ExecutiveSummaryFeedback | null;
  error?: ExecutiveSummaryError | null;
  onRetry?: () => void;
}

export const AISummary = ({
  chunks,
  isStreaming,
  permissionAI,
  isExpanded,
  onCollapse,
  initialFeedback,
  error,
  onRetry
}: AISummaryProps) => {
  const { replace } = useMarkdownSummary({ isExpanded, onCollapse });
  const { isFeedbackCtaEnabled } = useAppFeatures();
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);

  const isEmpty = chunks.length === 0;
  const summary = chunks.join("\n");
  const showFeedbackActions =
    isFeedbackCtaEnabled && !isStreaming && !isEmpty && !!latestAnalysisId;

  if (!permissionAI) {
    return (
      <div className="flex flex-col items-center justify-center p-6">
        <div className=" flex justify-center items-center w-12 h-12 rounded-full bg-[#F5F4ED] border border-[#EFEEEC]">
          <LockIcon className="w-8 h-8 fill-txtBody" />
        </div>
        <UnlockPremiumModal
          title={MESSAGE.UNLOCK_AI_SUMMARY_TITLE}
          contactUsDesc={MESSAGE.UNLOCK_AI_SUMMARY_CONTENT}
          classNameContainer="shadow-none border-0 relative top-0 left-0 transform-none w-full flex justify-center items-center"
          classNameContent="items-center text-center w-72"
        />
      </div>
    );
  }

  if (error) {
    return <AISummaryError error={error} onRetry={onRetry} />;
  }

  return (
    <div className="relative p-2 mx-5 markdown-ai-summary">
      {isEmpty && isStreaming ? (
        <div className="space-y-1">
          <p className="text-sm text-muted animate-pulse">
            Generating AI summary…
          </p>
          <ChunkSkeleton />
        </div>
      ) : (
        <>
          <Markdown content={summary} replace={replace} />
          {isStreaming && <ChunkSkeleton />}
        </>
      )}
      {showFeedbackActions && (
        <AISummaryFeedbackActions
          key={latestAnalysisId}
          referenceId={latestAnalysisId}
          summary={summary}
          initialFeedback={initialFeedback}
        />
      )}
    </div>
  );
};
